import { useEffect, useState } from "react";

const FALLBACK_CITY = "Near You";
const CACHE_KEY = "np_public_location";
const CACHE_TTL_MS = 30 * 60 * 1000;

const DEFAULT_LOCATION = {
  city: FALLBACK_CITY,
  lat: null,
  lng: null,
  has_location: false,
  resolved: false,
};

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached?.city || !cached?.cached_at || Date.now() - cached.cached_at > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return { ...DEFAULT_LOCATION, ...cached, has_location: true, resolved: true };
  } catch {}
  return null;
}

function writeCache(location) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...location, cached_at: Date.now() }));
  } catch {}
}

function cityFromResults(results) {
  const preferred = [
    "locality",
    "postal_town",
    "administrative_area_level_3",
    "administrative_area_level_2",
    "administrative_area_level_1",
  ];
  for (const type of preferred) {
    for (const result of results) {
      const component = (result.address_components || []).find((c) => c.types?.includes(type));
      if (component?.long_name) return component.long_name;
    }
  }
  return null;
}

export function usePublicLocation() {
  const [location, setLocation] = useState(() => readCache() || DEFAULT_LOCATION);

  useEffect(() => {
    if (readCache()) return undefined;
    if (!navigator.geolocation) {
      setLocation((current) => ({ ...current, resolved: true }));
      return undefined;
    }
    let cancelled = false;
    const finish = () => {
      if (!cancelled) setLocation((current) => ({ ...current, resolved: true }));
    };

    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!key) return finish();
        const { latitude, longitude } = position.coords;
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${key}`);
        const data = await response.json();
        const resolvedCity = data.status === "OK" ? cityFromResults(data.results || []) : null;
        if (!resolvedCity) return finish();
        const next = {
          city: resolvedCity,
          lat: latitude,
          lng: longitude,
          has_location: true,
          resolved: true,
        };
        writeCache(next);
        if (!cancelled) setLocation(next);
      } catch {
        finish();
      }
    }, finish, {
      enableHighAccuracy: false,
      maximumAge: 10 * 60 * 1000,
      timeout: 8000,
    });

    return () => { cancelled = true; };
  }, []);

  return location;
}

export function usePublicCity() {
  return usePublicLocation().city;
}

export async function resolveCheckoutCity() {
  const cached = readCache();
  if (cached?.has_location && cached.city) return cached.city;
  if (!navigator.geolocation) return null;
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const { latitude, longitude } = position.coords;
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${key}`);
        const data = await response.json();
        const city = data.status === "OK" ? cityFromResults(data.results || []) : null;
        if (city) {
          writeCache({ city, lat: latitude, lng: longitude, has_location: true, resolved: true });
        }
        resolve(city);
      } catch {
        resolve(null);
      }
    }, () => resolve(null), {
      enableHighAccuracy: false,
      maximumAge: 10 * 60 * 1000,
      timeout: 8000,
    });
  });
}
